import { useEffect, useMemo, useRef, useState } from "react";
import { Shield, Save, Loader2, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import MoneyField from "./MoneyField";
import { TimeInput24, hhmm, hourOf } from "./DateTime24";
import { fetchImpoundConfig, updateImpoundConfig } from "../lib/api";
import { toast } from "sonner";

// Compact money field with the $ prefix, always shown to the cent.
function MoneyInput({ label, value, onChange, testid }) {
  return (
    <MoneyField label={label} value={value} onChange={onChange} testid={testid} />
  );
}

export default function PoliceImpoundConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Track the originally-loaded config so we can show an "unsaved changes"
  // indicator on the Save button — dispatchers were missing the lone Save
  // button at the top of the card after editing fields lower down.
  const initialRef = useRef(null);

  useEffect(() => {
    fetchImpoundConfig()
      .then((c) => { setCfg(c); initialRef.current = JSON.stringify(c); })
      .catch(() => toast.error("Failed to load impound config"))
      .finally(() => setLoading(false));
  }, []);

  // Dirty if the current cfg differs from the snapshot we took on load.
  const dirty = useMemo(() => {
    if (!cfg || !initialRef.current) return false;
    return JSON.stringify(cfg) !== initialRef.current;
  }, [cfg]);

  if (loading || !cfg) {
    return <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-8 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 mr-2 inline animate-spin"/>Loading impound pricing…</div>;
  }
  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const updYard = (i, k, v) => setCfg((c) => {
    const ys = [...(c.yards || [])];
    // Pad with the same new-yard default the empty-slot view shows, otherwise
    // typing a name made BOTH checkboxes untick on screen while the backend
    // still saved them on — the form disagreed with what was stored.
    while (ys.length < 2) {
      ys.push({ name: "", address: "", lat: null, lng: null, auto_start_storage: true });
    }
    // Editing the address invalidates the cached coords — they'll be re-geocoded on save.
    const next = { ...ys[i], [k]: v };
    if (k === "address") { next.lat = null; next.lng = null; }
    if (next.auto_start_storage === undefined) next.auto_start_storage = true;
    ys[i] = next;
    return { ...c, yards: ys };
  });

  const save = async () => {
    setSaving(true);
    try {
      const next = await updateImpoundConfig({
        threshold_kg: cfg.threshold_kg,
        business_hours_start_hour: cfg.business_hours_start_hour,
        business_hours_end_hour: cfg.business_hours_end_hour,
        tow_fee_light_business: cfg.tow_fee_light_business,
        tow_fee_light_afterhours: cfg.tow_fee_light_afterhours,
        tow_fee_heavy_business: cfg.tow_fee_heavy_business,
        tow_fee_heavy_afterhours: cfg.tow_fee_heavy_afterhours,
        impound_fee_light_28d: cfg.impound_fee_light_28d,
        impound_fee_light_6mo: cfg.impound_fee_light_6mo,
        impound_fee_heavy_28d: cfg.impound_fee_heavy_28d,
        impound_fee_heavy_6mo: cfg.impound_fee_heavy_6mo,
        overstay_per_day_light: cfg.overstay_per_day_light,
        overstay_per_day_heavy: cfg.overstay_per_day_heavy,
        free_km: cfg.free_km,
        additional_km_rate: cfg.additional_km_rate,
        yards: (cfg.yards || []).slice(0, 2),
      });
      const n = next?._refreshed_count;
      // Strip transient fields before pushing into state so they don't appear
      // in subsequent edits.
      const { _refreshed_count, _refresh_failed, ...clean } = next;
      setCfg(clean);
      initialRef.current = JSON.stringify(clean);  // clear dirty after save
      if (typeof n === "number" && n > 0) {
        toast.success(`Impound pricing saved — ${n} active impound${n === 1 ? "" : "s"} re-priced`);
      } else {
        toast.success("Impound pricing saved");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-4" data-testid="impound-config-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-700 dark:text-blue-300" />
          <h3 className="font-display text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">Police Impound Pricing</h3>
        </div>
        <Button
          onClick={save}
          disabled={saving || !dirty}
          className={dirty
            ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white shadow-lg ring-2 ring-amber-300 dark:ring-amber-700 animate-pulse"
            : "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"}
          data-testid="impound-config-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin"/> : <Save className="h-3.5 w-3.5 mr-1.5"/>}
          {dirty ? "Save Changes" : "Saved"}
        </Button>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Live rates used by every new impound quote. Changes apply immediately to new jobs and resync to every active impound on save.</p>

      {/* Unsaved-changes banner — surfaces when the dispatcher has edited any
          field but not yet clicked Save. Solves the "I changed the price but
          impound fee didn't update" support reports caused by forgetting to
          confirm the change. */}
      {dirty && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-200" data-testid="unsaved-banner">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="font-semibold">You have unsaved pricing changes.</span>
          <span className="text-xs">Click Save Changes to apply them to all active impounds.</span>
        </div>
      )}

      {/* Impound Yards — up to 2. Round-trip km = yard → pickup → yard. */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Impound Yards (max 2)</div>
        <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-2">Drop-down options when creating an impound job. Distances auto-calculate as yard → pickup → yard. Coordinates resolve automatically when you save — edit the address any time the yard relocates.</p>
        <div className="grid grid-cols-2 gap-3">
          {[0, 1].map((i) => {
            // A yard the tenant hasn't created yet shows the new-yard default
            // (auto-start ON) so the checkbox matches what saving will store.
            const y = (cfg.yards && cfg.yards[i])
              || { name: "", address: "", lat: null, lng: null, auto_start_storage: true };
            return (
              <div key={i} className="border border-slate-200 dark:border-slate-700 rounded-md p-2 space-y-2" data-testid={`cfg-yard-${i}`}>
                <div>
                  <Label className="uppercase-label text-[10px]">Yard {i + 1} · Display Name</Label>
                  <Input value={y.name || ""} onChange={(e) => updYard(i, "name", e.target.value)} placeholder={`Yard ${i + 1}`} data-testid={`cfg-yard-${i}-name`}/>
                </div>
                <div>
                  <Label className="uppercase-label text-[10px]">Address</Label>
                  <Input value={y.address || ""} onChange={(e) => updYard(i, "address", e.target.value)} placeholder="Yard street address" data-testid={`cfg-yard-${i}-address`}/>
                </div>
                <label className="flex items-center gap-2 text-[11px] font-semibold text-slate-600 dark:text-slate-300 cursor-pointer select-none" data-testid={`cfg-yard-${i}-autostart-label`}>
                  <input
                    type="checkbox"
                    checked={!!y.auto_start_storage}
                    onChange={(e) => updYard(i, "auto_start_storage", e.target.checked)}
                    className="h-3.5 w-3.5 accent-blue-600"
                    data-testid={`cfg-yard-${i}-autostart`}
                  />
                  Auto-start storage clock on drop-off
                </label>
                <div className="text-[10px] text-slate-400 font-mono">
                  {y.lat != null && y.lng != null
                    ? `✓ Geocoded: ${Number(y.lat).toFixed(4)}, ${Number(y.lng).toFixed(4)}`
                    : "○ Will geocode on save"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Classification */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Classification</div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="uppercase-label text-[10px]">GVM Threshold (kg)</Label>
            <Input type="number" step="100" value={cfg.threshold_kg} onChange={(e)=>upd("threshold_kg", Number(e.target.value))} className="font-mono" data-testid="cfg-threshold"/>
          </div>
          <div>
            <Label className="uppercase-label text-[10px]">Biz Hours Start (24h)</Label>
            <TimeInput24
              value={hhmm(cfg.business_hours_start_hour)}
              onChange={(v)=>upd("business_hours_start_hour", hourOf(v))}
              dataTestId="cfg-bh-start"
            />
          </div>
          <div>
            <Label className="uppercase-label text-[10px]">Biz Hours End (24h)</Label>
            <TimeInput24
              value={hhmm(cfg.business_hours_end_hour)}
              onChange={(v)=>upd("business_hours_end_hour", hourOf(v))}
              dataTestId="cfg-bh-end"
            />
          </div>
        </div>
      </div>

      {/* Tow fees matrix */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Tow Fees</div>
        <div className="grid grid-cols-2 gap-2">
          <MoneyInput label="Light · Mon-Fri 7am-6pm" value={cfg.tow_fee_light_business} onChange={(v)=>upd("tow_fee_light_business", v)} testid="cfg-tow-lb"/>
          <MoneyInput label="Light · After-hours / Weekend / PH" value={cfg.tow_fee_light_afterhours} onChange={(v)=>upd("tow_fee_light_afterhours", v)} testid="cfg-tow-la"/>
          <MoneyInput label="Heavy · Mon-Fri 7am-6pm" value={cfg.tow_fee_heavy_business} onChange={(v)=>upd("tow_fee_heavy_business", v)} testid="cfg-tow-hb"/>
          <MoneyInput label="Heavy · After-hours / Weekend / PH" value={cfg.tow_fee_heavy_afterhours} onChange={(v)=>upd("tow_fee_heavy_afterhours", v)} testid="cfg-tow-ha"/>
        </div>
      </div>

      {/* Impound fees */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Impound Base Fees</div>
        <div className="grid grid-cols-2 gap-2">
          <MoneyInput label="Light · 28 days" value={cfg.impound_fee_light_28d} onChange={(v)=>upd("impound_fee_light_28d", v)} testid="cfg-imp-l28"/>
          <MoneyInput label="Light · 6 months" value={cfg.impound_fee_light_6mo} onChange={(v)=>upd("impound_fee_light_6mo", v)} testid="cfg-imp-l6"/>
          <MoneyInput label="Heavy · 28 days" value={cfg.impound_fee_heavy_28d} onChange={(v)=>upd("impound_fee_heavy_28d", v)} testid="cfg-imp-h28"/>
          <MoneyInput label="Heavy · 6 months" value={cfg.impound_fee_heavy_6mo} onChange={(v)=>upd("impound_fee_heavy_6mo", v)} testid="cfg-imp-h6"/>
        </div>
      </div>

      {/* Overstay + km */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Overstay & Additional Km</div>
        <div className="grid grid-cols-2 gap-2">
          <MoneyInput label="Overstay Light · per day" value={cfg.overstay_per_day_light} onChange={(v)=>upd("overstay_per_day_light", v)} testid="cfg-over-l"/>
          <MoneyInput label="Overstay Heavy · per day" value={cfg.overstay_per_day_heavy} onChange={(v)=>upd("overstay_per_day_heavy", v)} testid="cfg-over-h"/>
          <div>
            <Label className="uppercase-label text-[10px]">Free Km Allowance</Label>
            <Input type="number" step="0.1" value={cfg.free_km} onChange={(e)=>upd("free_km", Number(e.target.value))} className="font-mono" data-testid="cfg-free-km"/>
          </div>
          <MoneyInput label="Additional Km · per km" value={cfg.additional_km_rate} onChange={(v)=>upd("additional_km_rate", v)} testid="cfg-km-rate"/>
        </div>
      </div>

      {/* Bottom Save bar — duplicate of the top button so dispatchers don't
          have to scroll back up after editing fields lower in the card. */}
      <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {dirty
            ? "⚠ Changes pending — click Save to apply across all active impounds."
            : "✓ All changes saved."}
        </div>
        <Button
          onClick={save}
          disabled={saving || !dirty}
          className={dirty
            ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white shadow-lg ring-2 ring-amber-300 dark:ring-amber-700"
            : "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"}
          data-testid="impound-config-save-bottom"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin"/> : <Save className="h-3.5 w-3.5 mr-1.5"/>}
          {dirty ? "Save Changes" : "Saved"}
        </Button>
      </div>
    </div>
  );
}
